import { useEffect, useState } from "react";
import type { EmailConfigDto, EmailConfigRequest, EmailProviderType } from "../api/types";

interface EmailConfigFormProps {
  config: EmailConfigDto | null;
  loading: boolean;
  onSave: (body: EmailConfigRequest) => Promise<void>;
  onTest: (to: string) => Promise<{ ok: boolean; message: string }>;
  fallbackNotice?: string;
}

const PROVIDERS: { value: EmailProviderType; label: string }[] = [
  { value: "SMTP", label: "SMTP (your own mail server)" },
  { value: "SENDGRID", label: "SendGrid" },
  { value: "MAILGUN", label: "Mailgun" },
];

export function EmailConfigForm({ config, loading, onSave, onTest, fallbackNotice }: EmailConfigFormProps) {
  const [provider, setProvider] = useState<EmailProviderType>("SMTP");
  const [host, setHost] = useState("");
  const [port, setPort] = useState(587);
  const [username, setUsername] = useState("");
  const [domain, setDomain] = useState("");
  const [password, setPassword] = useState("");
  const [fromAddress, setFromAddress] = useState("");
  const [fromName, setFromName] = useState("");
  const [useTls, setUseTls] = useState(true);
  const [useSsl, setUseSsl] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (config) {
      setProvider(config.provider);
      setHost(config.host ?? "");
      setPort(config.port);
      setUsername(config.username ?? "");
      setDomain(config.domain ?? "");
      setFromAddress(config.fromAddress);
      setFromName(config.fromName ?? "");
      setUseTls(config.useTls);
      setUseSsl(config.useSsl);
    }
  }, [config]);

  async function handleSave() {
    setSaving(true);
    setSaveMessage(null);
    try {
      await onSave({
        provider,
        host: provider === "SMTP" ? host.trim() : null,
        port,
        username: provider === "SMTP" ? username.trim() || null : null,
        domain: provider === "MAILGUN" ? domain.trim() || null : null,
        password: password.trim() ? password.trim() : null,
        fromAddress: fromAddress.trim(),
        fromName: fromName.trim() || null,
        useTls,
        useSsl,
      });
      setPassword("");
      setSaveMessage("Saved.");
    } catch (err) {
      setSaveMessage(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!testTo.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await onTest(testTo.trim());
      setTestResult(res);
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : "Test send failed" });
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <p>Loading...</p>;

  const credentialLabel = provider === "SMTP" ? "Password" : "API key";

  return (
    <div>
      {!config && fallbackNotice && <p className="help-text">{fallbackNotice}</p>}
      {config && (
        <p className="help-text">
          {config.hasPassword ? `A ${credentialLabel.toLowerCase()} is currently set. ` : `No ${credentialLabel.toLowerCase()} set yet. `}
          Leave that field blank to keep it unchanged.
        </p>
      )}

      <div className="form-row">
        <label>Provider</label>
        <select value={provider} onChange={(e) => setProvider(e.target.value as EmailProviderType)}>
          {PROVIDERS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-grid">
        {provider === "SMTP" && (
          <>
            <div className="form-row">
              <label>SMTP host</label>
              <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.example.com" />
            </div>
            <div className="form-row">
              <label>Port</label>
              <input type="number" value={port} onChange={(e) => setPort(Number(e.target.value) || 0)} />
            </div>
            <div className="form-row">
              <label>Username</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} />
            </div>
          </>
        )}

        {provider === "MAILGUN" && (
          <div className="form-row">
            <label>Sending domain</label>
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="mg.yourdomain.com" />
            <span className="help-text">The domain you verified in your Mailgun account.</span>
          </div>
        )}

        <div className="form-row">
          <label>{credentialLabel}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={config?.hasPassword ? "•••••••• (unchanged)" : ""}
          />
        </div>
        <div className="form-row">
          <label>From address</label>
          <input value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} placeholder="reports@yourdomain.com" />
        </div>
        <div className="form-row">
          <label>From name</label>
          <input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="ReportForge" />
        </div>
      </div>

      {provider === "SMTP" && (
        <div className="form-row" style={{ maxWidth: "none" }}>
          <label style={{ fontWeight: 400 }}>
            <input type="checkbox" checked={useTls} onChange={(e) => setUseTls(e.target.checked)} style={{ marginRight: 6 }} />
            Use TLS (STARTTLS)
          </label>
          <label style={{ fontWeight: 400, marginTop: 6 }}>
            <input type="checkbox" checked={useSsl} onChange={(e) => setUseSsl(e.target.checked)} style={{ marginRight: 6 }} />
            Use SSL
          </label>
        </div>
      )}

      <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? "Saving..." : "Save"}
      </button>
      {saveMessage && <span className="help-text" style={{ marginLeft: 10 }}>{saveMessage}</span>}

      <div className="page-panel" style={{ marginTop: 16 }}>
        <h4>Send a test email</h4>
        <div className="form-row">
          <label>To</label>
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
        </div>
        <button type="button" className="btn-secondary" onClick={handleTest} disabled={testing}>
          {testing ? "Sending..." : "Send test email"}
        </button>
        {testResult && (
          <p className={testResult.ok ? "help-text" : "error-text"} style={{ marginTop: 8 }}>
            {testResult.message}
          </p>
        )}
      </div>
    </div>
  );
}
